import React, { useState } from 'react';
import { Spinner, Row, Col, Card, Table, Badge, Button } from 'react-bootstrap';
import Pagination from 'react-js-pagination';
import { useQuery } from 'react-query';
import { Get } from '@app/utils/httpProvider';
import {
    DANHSACH_DUYET_NGOAINGU_API, ACCOUNT_API, ACCOUNT_AUTHINFO_API
} from '@app/utils/apiUrl';
import { toast } from 'react-toastify';

const accountInfoCache = {};

const fetchAllItems = async (pageIndex, pageSize) => {
    const res = await Get(`${DANHSACH_DUYET_NGOAINGU_API.GET_THONGKE_NGOAINGU}?pageIndex=1&pageSize=1000000`);
    const data = res?.data || res;
    if (!data || !data.donViStats) return data;

    const rawStats = data.donViStats.$values || data.donViStats || [];
    const uniqueAccIds = [...new Set(rawStats.map(x => x.tenDonVi || x.TenDonVi))];
    const accDonViMap = {};

    const idsToFetch = uniqueAccIds.filter(id => !accountInfoCache[id]);

    if (idsToFetch.length > 0) {
        try {
            const payload = { PageIndex: 1, PageSize: 1000000 };
            const allRes = await Get(ACCOUNT_API.GET_PAGING_ACCOUNT_FROM_APP_DUNGCHUNG, null, payload);
            const allList = allRes?.data?.$values || allRes?.data?.data || allRes?.data || allRes || [];

            if (Array.isArray(allList)) {
                allList.forEach(info => {
                    const accId = info?.accountId || info?.AccountId;
                    if (!accId) return;
                    const donVis = info?.donVis || info?.DonVis?.$values || info?.DonVis || [];
                    const tenDonVi = (Array.isArray(donVis) && donVis[0]) ? (donVis[0].tenDonVi || donVis[0].TenDonVi) : 'Chưa có đơn vị';
                    accountInfoCache[accId] = tenDonVi;
                });
            }

            idsToFetch.forEach(id => {
                if (!accountInfoCache[id]) {
                    accountInfoCache[id] = 'Chưa có đơn vị';
                }
            });
        } catch (e) {
            console.error('Lỗi lấy AccountInfo hàng loạt:', e);
            idsToFetch.forEach(id => {
                accountInfoCache[id] = 'Chưa có đơn vị';
            });
        }
    }

    uniqueAccIds.forEach((accId) => {
        accDonViMap[accId] = accountInfoCache[accId] || 'Chưa có đơn vị';
    });

    const groupedMap = {};
    for (let stat of rawStats) {
        const accId = stat.tenDonVi || stat.TenDonVi;
        const realDonVi = accDonViMap[accId] || 'Chưa có đơn vị';

        if (!groupedMap[realDonVi]) {
            groupedMap[realDonVi] = {
                tenDonVi: realDonVi,
                soNguoi: 0,
                soPhuHop: 0,
                soKhongPhuHop: 0,
                soChuaDuyet: 0
            };
        }

        groupedMap[realDonVi].soNguoi += (stat.soNguoi || stat.SoNguoi || 0);
        groupedMap[realDonVi].soPhuHop += (stat.soPhuHop || stat.SoPhuHop || 0);
        groupedMap[realDonVi].soKhongPhuHop += (stat.soKhongPhuHop || stat.SoKhongPhuHop || 0);
        groupedMap[realDonVi].soChuaDuyet += (stat.soChuaDuyet || stat.SoChuaDuyet || 0);
    }

    let groupedList = Object.values(groupedMap).sort((a, b) => b.soNguoi - a.soNguoi);

    data.totalDonVi = groupedList.length;
    data.donViStats = groupedList.slice((pageIndex - 1) * pageSize, pageIndex * pageSize);

    return data;
};

// ─── Stat Card ───────────────────────────────────────────────────────────────

const StatCard = ({ icon, label, value, subtitle, bg }) => (
    <div
        style={{
            background: bg || 'linear-gradient(135deg,#667eea,#764ba2)',
            borderRadius: 18,
            padding: '22px 26px',
            color: '#fff',
            boxShadow: '0 6px 24px rgba(0,0,0,0.13)',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            height: '100%',
            minHeight: 130,
            position: 'relative',
            overflow: 'hidden',
        }}
    >
        {/* Icon nền mờ góc phải */}
        <div style={{
            position: 'absolute',
            right: 16,
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: 56,
            opacity: 0.18,
            lineHeight: 1,
            pointerEvents: 'none',
        }}>
            {icon}
        </div>

        {/* Icon nhỏ hiển thị */}
        <div style={{ fontSize: 26, opacity: 0.9, lineHeight: 1 }}>{icon}</div>
        <div style={{ fontSize: 13, fontWeight: 500, opacity: 0.88, letterSpacing: 0.3, marginTop: 2 }}>
            {label}
        </div>
        <div style={{ fontSize: 46, fontWeight: 800, lineHeight: 1.05, letterSpacing: -1 }}>
            {value}
        </div>
        {subtitle && (
            <div style={{ fontSize: 11.5, opacity: 0.78, marginTop: 2 }}>{subtitle}</div>
        )}
    </div>
);

// ─── Helper Format Ngày ──────────────────────────────────────────────────────
const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('vi-VN');
};

// ─── Helper Format Loại văn bằng ─────────────────────────────────────────────
const formatLoaiVanBang = (type) => {
    if (!type) return '-';
    const s = String(type).trim();
    if (s === 'NuocNgoai') return 'Văn bằng nước ngoài';
    if (s === 'VanBangTrongNuoc') return 'Văn bằng trong nước (ngoại ngữ)';
    return s;
};

// ─── Helper Format Điểm Số ────────────────────────────────────────────────────
const formatDiemSo = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return Number(value).toLocaleString('vi-VN');
};

// ─── Helper Kiểm Tra Đã Duyệt ────────────────────────────────────────────────
const checkItemDaDuyet = (item) => {
    if (!item) return false;
    const status = item.trangThai ?? item.TrangThai;
    if (status === null || status === undefined || status === '') return false;
    const strStatus = String(status).toLowerCase().trim();
    return strStatus === '2' || strStatus === 'daduyet';
};

// ─── Helper Kiểm Tra Đạt Yêu Cầu ─────────────────────────────────────────────
const checkItemDatYeuCau = (item) => {
    if (!item) return false;
    const status = item.trangThai ?? item.TrangThai;
    if (status !== null && status !== undefined && status !== '') {
        const strStatus = String(status).toLowerCase().trim();
        if (strStatus === '0' || strStatus === 'nhaplieu') return false; // Không tính nếu là bản nháp
        if (strStatus === '3' || strStatus === 'tuchoi') return false;   // Không tính nếu bị từ chối
    }
    const val = item.xacNhanDatYeuCau ?? item.XacNhanDatYeuCau;
    if (val === null || val === undefined || val === '') return false;
    const str = String(val).toLowerCase().trim();
    return str === 'đạt' || str === 'dat' || str === 'true' || str === '1';
};

// ─── Main Component ──────────────────────────────────────────────────────────

const ThongKe = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [isExporting, setIsExporting] = useState(false);
    const [isExportingDonVi, setIsExportingDonVi] = useState(false);
    const [isExportingDuyet, setIsExportingDuyet] = useState(false);
    const itemsPerPage = 5;

    const { data, isLoading, isError } = useQuery(
        ['thongKeNgoaiNgu', currentPage],
        () => fetchAllItems(currentPage, itemsPerPage),
        { cacheTime: 0, staleTime: 0, keepPreviousData: true }
    );

    const {
        soHoSo = 0,
        totalVBCC = 0,
        hoSoPhuHop = 0,
        hoSoKhongPhuHop = 0,
        hoSoChuaDuyet = 0,
        totalDonVi = 0,
        donViStats = []
    } = data || {};

    const totalPages = Math.ceil(totalDonVi / itemsPerPage);

    // ── Hàm export thống kê theo Đơn vị ───────────────────────────────────
    const handleExportThongKeDonVi = async () => {
        try {
            setIsExportingDonVi(true);

            // 1. Lấy tất cả account kèm thông tin đơn vị
            const infoRes = await Get(ACCOUNT_API.GET_PAGING_ACCOUNT_FROM_APP_DUNGCHUNG, null, { Keyword: '', PageIndex: 1, PageSize: 1000000 });
            const infoData = infoRes?.data?.$values || infoRes?.data || infoRes || [];
            const infoList = Array.isArray(infoData) ? infoData : [];

            // 2. Nhóm tổng số người và tổng số bác sĩ theo đơn vị
            const donViTotalMap = {}; // donVi -> tổng số người
            const accDonViMap = {};   // accId(lowercase) -> tenDonVi
            const donViBacSiTotalMap = {}; // donVi -> tổng số bác sĩ
            const accIsBacSiMap = {}; // accId(lowercase) -> boolean
            infoList.forEach(info => {
                const rawId = info?.accountId || info?.AccountId || info?.id || info?.Id;
                if (!rawId) return;
                const accId = String(rawId).toLowerCase();
                const donVis = info?.donVis || info?.DonVis?.$values || info?.DonVis || [];
                const donVi = (Array.isArray(donVis) && donVis[0])
                    ? (donVis[0].tenDonVi || donVis[0].TenDonVi || 'Chưa có đơn vị')
                    : 'Chưa có đơn vị';
                accDonViMap[accId] = donVi;
                donViTotalMap[donVi] = (donViTotalMap[donVi] || 0) + 1;

                const chucDanh = [
                    info?.doiTuong?.tenDoiTuong,
                    info?.DoiTuong?.TenDoiTuong,
                    info?.chucDanhChuyenMon?.tenDoiTuong,
                    info?.ChucDanhChuyenMon?.TenDoiTuong,
                    info?.chucDanh?.tenChucDanh,
                    info?.ChucDanh?.TenChucDanh
                ].filter(Boolean).join(' ').toLowerCase();

                const isBacSi = chucDanh.includes('bác sĩ') || chucDanh.includes('bác sỹ') || chucDanh.includes('bac si') || chucDanh.includes('bacsi');
                accIsBacSiMap[accId] = isBacSi;
                if (isBacSi) {
                    donViBacSiTotalMap[donVi] = (donViBacSiTotalMap[donVi] || 0) + 1;
                }
            });

            // 3. Lấy tất cả văn bằng + chứng chỉ (loại trừ NhapLieu=0)
            const [vanBangRes, chungChiRes] = await Promise.all([
                Get(DANHSACH_DUYET_NGOAINGU_API.GET_ALL_VANBANG_BY_PAGING, null, { pageIndex: 1, pageSize: 1000000, keyword: '' }),
                Get(DANHSACH_DUYET_NGOAINGU_API.GET_ALL_CHUNGCHINGOAINGU_BY_PAGING, null, { pageIndex: 1, pageSize: 1000000, keyword: '' })
            ]);

            const isNonNhapLieu = (item) => {
                const status = item.trangThai ?? item.TrangThai;
                if (status === null || status === undefined) return true;
                if (!isNaN(status)) return Number(status) !== 0;
                const str = String(status).toLowerCase().trim();
                return str !== 'nhaplieu' && str !== '0';
            };

            const rawVb = vanBangRes?.data?.$values || vanBangRes?.data?.data || (Array.isArray(vanBangRes?.data) ? vanBangRes.data : []) || [];
            const rawCc = chungChiRes?.data?.$values || chungChiRes?.data?.data || (Array.isArray(chungChiRes?.data) ? chungChiRes.data : []) || [];

            const vbItems = rawVb.filter(isNonNhapLieu);
            const ccItems = rawCc.filter(isNonNhapLieu);

            // Gom danh sách bằng cấp/chứng chỉ theo AccountId
            const accountItemsMap = {};
            [...vbItems, ...ccItems].forEach(item => {
                const rawId = item.accountId || item.AccountId;
                if (!rawId) return;
                const accId = String(rawId).toLowerCase();
                if (!accountItemsMap[accId]) {
                    accountItemsMap[accId] = [];
                }
                accountItemsMap[accId].push(item);
            });


            // 4. Tính toán số lượng theo từng đơn vị
            const donViDaDuyetMap = {};        // donVi -> số người đã được duyệt
            const donViBacSiDaDuyetMap = {};    // donVi -> số BS đã được duyệt
            const donViBacSiDatYeuCauMap = {};  // donVi -> số BS đạt yêu cầu

            Object.entries(accDonViMap).forEach(([accId, donVi]) => {
                const items = accountItemsMap[accId] || [];
                const isDaDuyet = items.some(checkItemDaDuyet);
                const isDatYeuCau = items.some(checkItemDatYeuCau);
                const isBacSi = !!accIsBacSiMap[accId];

                if (isDaDuyet) {
                    donViDaDuyetMap[donVi] = (donViDaDuyetMap[donVi] || 0) + 1;
                    if (isBacSi) {
                        donViBacSiDaDuyetMap[donVi] = (donViBacSiDaDuyetMap[donVi] || 0) + 1;
                    }
                }

                if (isBacSi && isDatYeuCau) {
                    donViBacSiDatYeuCauMap[donVi] = (donViBacSiDatYeuCauMap[donVi] || 0) + 1;
                }
            });

            // 5. Tổng hợp và sắp xếp theo tên đơn vị (tiếng Việt)
            const allDonVis = [...new Set([
                ...Object.keys(donViTotalMap),
                ...Object.keys(donViDaDuyetMap),
                ...Object.keys(donViBacSiTotalMap)
            ])].sort((a, b) => a.localeCompare(b, 'vi'));

            if (allDonVis.length === 0) {
                toast.warning('Không có dữ liệu đơn vị để xuất');
                return;
            }

            // 6. Build HTML table
            let tableHTML = `
                <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
                <head>
                    <meta charset="UTF-8">
                    <!--[if gte mso 9]><xml>
                        <x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>
                            <x:Name>ThongKeDonVi</x:Name>
                            <x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
                        </x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook>
                    </xml><![endif]-->
                    <style>
                        table { border-collapse: collapse; font-family: 'Times New Roman', Arial, sans-serif; font-size: 11pt; }
                        th { background-color: #dce6f1; font-weight: bold; text-align: center; vertical-align: middle; border: 1px solid #000; padding: 6px 10px; }
                        td { border: 1px solid #000; padding: 5px 8px; vertical-align: middle; }
                    </style>
                </head>
                <body>
                    <table border="1" style="border-collapse:collapse;">
                        <thead>
                            <tr>
                                <th style="background-color:#dce6f1;text-align:center;font-weight:bold;border:1px solid #000;">STT</th>
                                <th style="background-color:#dce6f1;text-align:center;font-weight:bold;border:1px solid #000;">Đơn vị</th>
                                <th style="background-color:#dce6f1;text-align:center;font-weight:bold;border:1px solid #000;">Số lượng người trong Đơn vị</th>
                                <th style="background-color:#dce6f1;text-align:center;font-weight:bold;border:1px solid #000;">Số người đã được duyệt</th>
                                <th style="background-color:#dce6f1;text-align:center;font-weight:bold;border:1px solid #000;">Số người không được duyệt</th>
                                <th style="background-color:#dce6f1;text-align:center;font-weight:bold;border:1px solid #000;">Số lượng Bác sĩ của khoa/ban</th>
                                <th style="background-color:#dce6f1;text-align:center;font-weight:bold;border:1px solid #000;">Số BS đã được duyệt</th>
                                <th style="background-color:#dce6f1;text-align:center;font-weight:bold;border:1px solid #000;">Số BS đạt yêu cầu</th>
                            </tr>
                        </thead>
                        <tbody>
            `;

            let totalNguoi = 0;
            let totalDaDuyet = 0;
            let totalKhongDuyet = 0;
            let totalBacSi = 0;
            let totalBacSiDaDuyet = 0;
            let totalBacSiDatYeuCau = 0;

            allDonVis.forEach((donVi, idx) => {
                const soLuong = donViTotalMap[donVi] || 0;
                const daDuyet = donViDaDuyetMap[donVi] || 0;
                const khongDuyet = Math.max(soLuong - daDuyet, 0);
                const soBacSi = donViBacSiTotalMap[donVi] || 0;
                const bacSiDaDuyet = donViBacSiDaDuyetMap[donVi] || 0;
                const bacSiDatYeuCau = donViBacSiDatYeuCauMap[donVi] || 0;

                totalNguoi += soLuong;
                totalDaDuyet += daDuyet;
                totalKhongDuyet += khongDuyet;
                totalBacSi += soBacSi;
                totalBacSiDaDuyet += bacSiDaDuyet;
                totalBacSiDatYeuCau += bacSiDatYeuCau;

                tableHTML += `
                    <tr>
                        <td style="text-align:center;vertical-align:middle;border:1px solid #000;">${idx + 1}</td>
                        <td style="vertical-align:middle;border:1px solid #000;">${donVi}</td>
                        <td style="text-align:center;vertical-align:middle;border:1px solid #000;">${soLuong}</td>
                        <td style="text-align:center;vertical-align:middle;border:1px solid #000;">${daDuyet}</td>
                        <td style="text-align:center;vertical-align:middle;border:1px solid #000;">${khongDuyet}</td>
                        <td style="text-align:center;vertical-align:middle;border:1px solid #000;">${soBacSi}</td>
                        <td style="text-align:center;vertical-align:middle;border:1px solid #000;">${bacSiDaDuyet}</td>
                        <td style="text-align:center;vertical-align:middle;border:1px solid #000;">${bacSiDatYeuCau}</td>
                    </tr>
                `;
            });

            // Hàng tổng cộng
            tableHTML += `
                    <tr style="background-color:#f2f2f2;font-weight:bold;">
                        <td style="text-align:center;vertical-align:middle;border:1px solid #000;" colspan="2">TỔNG CỘNG</td>
                        <td style="text-align:center;vertical-align:middle;border:1px solid #000;">${totalNguoi}</td>
                        <td style="text-align:center;vertical-align:middle;border:1px solid #000;">${totalDaDuyet}</td>
                        <td style="text-align:center;vertical-align:middle;border:1px solid #000;">${totalKhongDuyet}</td>
                        <td style="text-align:center;vertical-align:middle;border:1px solid #000;">${totalBacSi}</td>
                        <td style="text-align:center;vertical-align:middle;border:1px solid #000;">${totalBacSiDaDuyet}</td>
                        <td style="text-align:center;vertical-align:middle;border:1px solid #000;">${totalBacSiDatYeuCau}</td>
                    </tr>
                        </tbody>
                    </table>
                </body>
                </html>
            `;

            const blob = new Blob(['\ufeff' + tableHTML], { type: 'application/vnd.ms-excel;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `ThongKe_TheoDonVi_${Date.now()}.xls`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            toast.success('Xuất Excel thống kê đơn vị thành công!');
        } catch (error) {
            console.error('[Export ĐV] Lỗi:', error);
            toast.error('Có lỗi xảy ra khi xuất Excel');
        } finally {
            setIsExportingDonVi(false);
        }
    };

    // ── Hàm export tổng hợp base (dùng chung cho Đạt yêu cầu & Đã duyệt) ─────
    const executeExportTongHop = async ({
        filterFn,
        emptyMessage,
        fileNamePrefix,
        sheetName,
        successMessage,
        setLoading
    }) => {
        try {
            setLoading(true);

            // 1. Lấy tất cả văn bằng theo điều kiện lọc
            const vanBangRes = await Get(DANHSACH_DUYET_NGOAINGU_API.GET_ALL_VANBANG_BY_PAGING, null, { pageIndex: 1, pageSize: 1000000, keyword: '' });
            let vanBangItems = (vanBangRes?.data?.$values || vanBangRes?.data?.data || (Array.isArray(vanBangRes?.data) ? vanBangRes.data : []))
                .map(item => ({ ...item, _loai: 'vanBang' }))
                .filter(filterFn);

            // 2. Lấy tất cả chứng chỉ ngoại ngữ theo điều kiện lọc
            const chungChiRes = await Get(DANHSACH_DUYET_NGOAINGU_API.GET_ALL_CHUNGCHINGOAINGU_BY_PAGING, null, { pageIndex: 1, pageSize: 1000000, keyword: '' });
            let chungChiItems = (chungChiRes?.data?.$values || chungChiRes?.data?.data || (Array.isArray(chungChiRes?.data) ? chungChiRes.data : []))
                .map(item => ({ ...item, _loai: 'chungChi' }))
                .filter(filterFn);

            const allItems = [...vanBangItems, ...chungChiItems];

            if (allItems.length === 0) {
                toast.warning(emptyMessage);
                return;
            }

            // 3. Nhóm theo AccountId (normalize về lowercase để tránh case-mismatch)
            const groupedMap = {};
            allItems.forEach(item => {
                const rawId = item.accountId || item.AccountId;
                if (!rawId) return;
                const accId = String(rawId).toLowerCase();
                if (!groupedMap[accId]) {
                    groupedMap[accId] = { accountId: rawId, vanBangs: [], chungChis: [] };
                }
                if (item._loai === 'vanBang') groupedMap[accId].vanBangs.push(item);
                else groupedMap[accId].chungChis.push(item);
            });

            const uniqueAccIds = Object.keys(groupedMap);

            // 4. Bulk fetch AccountAuthInfo (họ tên, năm sinh) — dùng lowercase key
            const accountAuthMap = new Map();
            try {
                const authRes = await Get(ACCOUNT_AUTHINFO_API.GET_PAGING, null, { pageIndex: 1, pageSize: 1000000 });
                const authData = authRes?.data?.data || authRes?.data?.$values || authRes?.data || authRes || [];
                const authList = Array.isArray(authData) ? authData : [];
                authList.forEach(acc => {
                    const rawId = acc.accountId || acc.AccountId || acc.id || acc.Id;
                    if (!rawId) return;
                    const accId = String(rawId).toLowerCase();
                    const dob = acc.dateOfBirth || acc.DateOfBirth;
                    accountAuthMap.set(accId, {
                        lastName: acc.lastName || acc.LastName || '',
                        middleName: acc.middleName || acc.MiddleName || '',
                        firstName: acc.firstName || acc.FirstName || '',
                        yearOfBirth: dob ? new Date(dob).getFullYear() : '-'
                    });
                });
            } catch (e) {
                console.error('[Export TH] Lỗi fetch AccountAuthInfo:', e);
            }

            // 5. Bulk fetch AccountInfo (chức danh, đơn vị) — dùng lowercase key
            const accountInfoMap = new Map();
            try {
                const infoRes = await Get(ACCOUNT_API.GET_PAGING_ACCOUNT_FROM_APP_DUNGCHUNG, null, { Keyword: '', PageIndex: 1, PageSize: 1000000 });
                const infoData = infoRes?.data?.$values || infoRes?.data || infoRes || [];
                const infoList = Array.isArray(infoData) ? infoData : [];
                infoList.forEach(info => {
                    const rawId = info?.accountId || info?.AccountId;
                    if (!rawId) return;
                    const accId = String(rawId).toLowerCase();
                    const donVis = info?.donVis || info?.DonVis?.$values || info?.DonVis || [];
                    const donVi = (Array.isArray(donVis) && donVis[0]) ? (donVis[0].tenDonVi || donVis[0].TenDonVi) : '-';
                    accountInfoMap.set(accId, {
                        chucDanh: info?.doiTuong?.tenDoiTuong || info?.DoiTuong?.TenDoiTuong || info?.chucDanhChuyenMon?.tenDoiTuong || info?.ChucDanhChuyenMon?.TenDoiTuong || '-',
                        donVi
                    });
                });
            } catch (e) {
                console.error('[Export TH] Lỗi fetch AccountInfo:', e);
            }

            // 5b. Fallback: với những accId chưa tìm được trong bulk fetch
            const missingAccIds = uniqueAccIds.filter(accId => !accountAuthMap.has(accId) || !accountInfoMap.has(accId));
            if (missingAccIds.length > 0) {
                await Promise.all(missingAccIds.map(async (accId) => {
                    const rawId = groupedMap[accId]?.accountId || accId;
                    try {
                        const [authRes, detailRes] = await Promise.all([
                            Get(`${ACCOUNT_AUTHINFO_API.GET_BY_ID_ACCOUNT}/${rawId}`),
                            Get(`${ACCOUNT_API.GET_ACCOUNT_INFO_BYID_FROM_APP_DUNGCHUNG}/${rawId}`)
                        ]);
                        const auth = authRes?.data || authRes;
                        const detail = detailRes?.data || detailRes;

                        if (auth && !accountAuthMap.has(accId)) {
                            const dob = auth.dateOfBirth || auth.DateOfBirth;
                            accountAuthMap.set(accId, {
                                lastName: auth.lastName || auth.LastName || '',
                                middleName: auth.middleName || auth.MiddleName || '',
                                firstName: auth.firstName || auth.FirstName || '',
                                yearOfBirth: dob ? new Date(dob).getFullYear() : '-'
                            });
                        }

                        if (detail && !accountInfoMap.has(accId)) {
                            const donVis = detail?.donVis || detail?.DonVis?.$values || detail?.DonVis || [];
                            const donVi = (Array.isArray(donVis) && donVis[0]) ? (donVis[0].tenDonVi || donVis[0].TenDonVi) : '-';
                            accountInfoMap.set(accId, {
                                chucDanh: detail?.doiTuong?.tenDoiTuong || detail?.DoiTuong?.TenDoiTuong || detail?.chucDanhChuyenMon?.tenDoiTuong || detail?.ChucDanhChuyenMon?.TenDoiTuong || '-',
                                donVi
                            });
                        }
                    } catch (e) {
                        console.warn(`[Export TH] Fallback fetch thất bại cho accId=${rawId}:`, e);
                    }
                }));
            }

            // 6b. Sắp xếp theo Đơn vị (A→Z), phụ theo Họ tên (A→Z)
            const sortedAccIds = [...uniqueAccIds].sort((a, b) => {
                const donViA = (accountInfoMap.get(a)?.donVi || '-').toLowerCase();
                const donViB = (accountInfoMap.get(b)?.donVi || '-').toLowerCase();
                if (donViA !== donViB) return donViA.localeCompare(donViB, 'vi');

                // Cùng đơn vị → sắp xếp theo họ tên
                const authA = accountAuthMap.get(a);
                const authB = accountAuthMap.get(b);
                const nameA = [authA?.lastName, authA?.middleName, authA?.firstName].filter(Boolean).join(' ').toLowerCase();
                const nameB = [authB?.lastName, authB?.middleName, authB?.firstName].filter(Boolean).join(' ').toLowerCase();
                return nameA.localeCompare(nameB, 'vi');
            });

            // 6. Build HTML table
            let tableHTML = `
                <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
                <head>
                    <meta charset="UTF-8">
                    <!--[if gte mso 9]><xml>
                        <x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>
                            <x:Name>${sheetName}</x:Name>
                            <x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
                        </x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook>
                    </xml><![endif]-->
                    <style>
                        table { border-collapse: collapse; font-family: 'Times New Roman', Arial, sans-serif; font-size: 11pt; }
                        th { background-color: #dce6f1; font-weight: bold; text-align: center; vertical-align: middle; border: 1px solid #000; padding: 6px 10px; }
                        td { border: 1px solid #000; padding: 5px 8px; vertical-align: middle; }
                    </style>
                </head>
                <body>
                    <table border="1" style="border-collapse:collapse;">
                        <thead>
                            <tr>
                                <th style="background-color:#dce6f1;text-align:center;font-weight:bold;border:1px solid #000;">STT</th>
                                <th style="background-color:#dce6f1;text-align:center;font-weight:bold;border:1px solid #000;">Họ và tên</th>
                                <th style="background-color:#dce6f1;text-align:center;font-weight:bold;border:1px solid #000;">Năm sinh</th>
                                <th style="background-color:#dce6f1;text-align:center;font-weight:bold;border:1px solid #000;">Chức danh</th>
                                <th style="background-color:#dce6f1;text-align:center;font-weight:bold;border:1px solid #000;">Đơn vị</th>
                                <th style="background-color:#dce6f1;text-align:center;font-weight:bold;border:1px solid #000;">Tên văn bằng</th>
                                <th style="background-color:#dce6f1;text-align:center;font-weight:bold;border:1px solid #000;">Loại văn bằng</th>
                                <th style="background-color:#dce6f1;text-align:center;font-weight:bold;border:1px solid #000;">Đơn vị cấp VB</th>
                                <th style="background-color:#dce6f1;text-align:center;font-weight:bold;border:1px solid #000;">Tên chứng chỉ</th>
                                <th style="background-color:#dce6f1;text-align:center;font-weight:bold;border:1px solid #000;">Trình độ</th>
                                <th style="background-color:#dce6f1;text-align:center;font-weight:bold;border:1px solid #000;">Điểm số</th>
                                <th style="background-color:#dce6f1;text-align:center;font-weight:bold;border:1px solid #000;">Đơn vị cấp CC</th>
                            </tr>
                        </thead>
                        <tbody>
            `;

            let stt = 1;
            sortedAccIds.forEach(accId => {
                const group = groupedMap[accId];
                const authInfo = accountAuthMap.get(accId) || { lastName: '', middleName: '', firstName: '', yearOfBirth: '-' };
                const detailInfo = accountInfoMap.get(accId) || { chucDanh: '-', donVi: '-' };

                const fullName = [authInfo.lastName, authInfo.middleName, authInfo.firstName]
                    .filter(v => v && String(v).trim() !== '')
                    .join(' ') || '-';
                const yearOfBirth = authInfo.yearOfBirth;
                const chucDanh = detailInfo.chucDanh;
                const donVi = detailInfo.donVi;

                const vbs = group.vanBangs;
                const ccs = group.chungChis;
                const rowCount = Math.max(vbs.length, ccs.length, 1);

                for (let i = 0; i < rowCount; i++) {
                    const vb = vbs[i] || null;
                    const cc = ccs[i] || null;

                    const vbDonVi = vb ? (vb.coSoCapBang || vb.CoSoCapBang || '-') : '';
                    const ccDonVi = cc ? (cc.donViCap || cc.DonViCap || '-') : '';

                    tableHTML += '<tr>';
                    if (i === 0) {
                        tableHTML += `
                            <td rowspan="${rowCount}" style="vertical-align:middle;text-align:center;border:1px solid #000;">${stt++}</td>
                            <td rowspan="${rowCount}" style="vertical-align:middle;border:1px solid #000;">${fullName}</td>
                            <td rowspan="${rowCount}" style="vertical-align:middle;text-align:center;border:1px solid #000;">${yearOfBirth}</td>
                            <td rowspan="${rowCount}" style="vertical-align:middle;border:1px solid #000;">${chucDanh}</td>
                            <td rowspan="${rowCount}" style="vertical-align:middle;border:1px solid #000;">${donVi}</td>
                        `;
                    }
                    tableHTML += `
                        <td style="vertical-align:middle;border:1px solid #000;">${vb ? (vb.tenVanBang || vb.TenVanBang || '-') : ''}</td>
                        <td style="vertical-align:middle;border:1px solid #000;">${vb ? formatLoaiVanBang(vb.loaiVanBang || vb.LoaiVanBang) : ''}</td>
                        <td style="vertical-align:middle;border:1px solid #000;">${vbDonVi}</td>
                        <td style="vertical-align:middle;border:1px solid #000;">${cc ? (cc.tenChungChi || cc.TenChungChi || '-') : ''}</td>
                        <td style="vertical-align:middle;text-align:center;border:1px solid #000;">${cc ? (cc.trinhDo || cc.TrinhDo || '-') : ''}</td>
                        <td style="vertical-align:middle;text-align:center;border:1px solid #000;">${cc ? formatDiemSo(cc.diemSo ?? cc.DiemSo) : ''}</td>
                        <td style="vertical-align:middle;border:1px solid #000;">${ccDonVi}</td>
                    </tr>
                    `;
                }
            });

            if (stt === 1) {
                toast.warning(emptyMessage);
                return;
            }

            tableHTML += `
                        </tbody>
                    </table>
                </body>
                </html>
            `;

            const blob = new Blob(['\ufeff' + tableHTML], { type: 'application/vnd.ms-excel;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${fileNamePrefix}_${Date.now()}.xls`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            toast.success(successMessage);
        } catch (error) {
            console.error('[Export TH] Lỗi:', error);
            toast.error('Có lỗi xảy ra khi xuất Excel');
        } finally {
            setLoading(false);
        }
    };

    // ── Hàm export tổng hợp người đạt yêu cầu ───────────────────────────
    const handleExportTongHop = () => {
        return executeExportTongHop({
            filterFn: checkItemDatYeuCau,
            emptyMessage: 'Không có dữ liệu người đạt yêu cầu để xuất Excel',
            fileNamePrefix: 'TongHop_NhanSu_DatYeuCau_NgoaiNgu',
            sheetName: 'NhanSuDatYeuCau',
            successMessage: 'Xuất Excel danh sách đạt yêu cầu thành công!',
            setLoading: setIsExporting
        });
    };

    // ── Hàm export tổng hợp người đã được duyệt ──────────────────────────
    const handleExportTongHopDuyet = () => {
        return executeExportTongHop({
            filterFn: checkItemDaDuyet,
            emptyMessage: 'Không có dữ liệu người đã được duyệt để xuất Excel',
            fileNamePrefix: 'TongHop_NhanSu_DaDuyet_NgoaiNgu',
            sheetName: 'NhanSuDaDuyet',
            successMessage: 'Xuất Excel danh sách đã duyệt thành công!',
            setLoading: setIsExportingDuyet
        });
    };

    if (isLoading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted fw-semibold">Đang tải dữ liệu thống kê...</p>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="text-center py-5 text-danger">
                <i className="fas fa-exclamation-triangle fa-2x mb-2" />
                <p>Không thể tải dữ liệu thống kê. Vui lòng thử lại.</p>
            </div>
        );
    }

    return (
        <div className="py-3">
            {/* ── KPI Cards ── */}
            <Row className="g-3 mb-4">
                <Col xs={12} sm={6} md={3}>
                    <StatCard
                        icon="👤"
                        label="Tổng số hồ sơ đã cập nhật"
                        value={soHoSo}
                        subtitle={`${totalVBCC} văn bằng / chứng chỉ`}
                        bg="linear-gradient(135deg,#667eea,#764ba2)"
                    />
                </Col>
                <Col xs={12} sm={6} md={3}>
                    <StatCard
                        icon="✅"
                        label="Tổng số hồ sơ Phù hợp"
                        value={hoSoPhuHop}
                        subtitle="Có ít nhất 1 văn bằng/chứng chỉ Đạt yêu cầu"
                        bg="linear-gradient(135deg,#11998e,#38ef7d)"
                    />
                </Col>
                <Col xs={12} sm={6} md={3}>
                    <StatCard
                        icon="❌"
                        label="Tổng số hồ sơ Không phù hợp"
                        value={hoSoKhongPhuHop}
                        subtitle="Tất cả văn bằng & chứng chỉ đều Không đạt"
                        bg="linear-gradient(135deg,#e53935,#e35d5b)"
                    />
                </Col>
                <Col xs={12} sm={6} md={3}>
                    <StatCard
                        icon="⏳"
                        label="Tổng số hồ sơ Chưa duyệt"
                        value={hoSoChuaDuyet}
                        subtitle="Có ít nhất 1 văn bằng/chứng chỉ Chưa được duyệt"
                        bg="linear-gradient(135deg,#ff9800,#e65c00)"
                    />
                </Col>
            </Row>


            {/* ── Bảng theo đơn vị ── */}
            <Card className="shadow-sm border-0" style={{ borderRadius: 14 }}>
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="fw-bold mb-0 text-dark">
                            <i className="fas fa-building me-2 text-primary" />
                            Số lượng người nộp hồ sơ theo đơn vị
                        </h6>
                        <div className="d-flex align-items-center gap-2">
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={handleExportThongKeDonVi}
                                disabled={isExportingDonVi}
                                className="d-flex align-items-center gap-2 shadow-sm"
                                title="Xuất thống kê số lượng người theo từng đơn vị"
                            >
                                {isExportingDonVi ? <Spinner animation="border" size="sm" /> : <i className="fas fa-file-excel" />}
                                {isExportingDonVi ? 'Đang xuất...' : 'Xuất Excel theo Đơn vị'}
                            </Button>
                            <Button
                                variant="success"
                                size="sm"
                                onClick={handleExportTongHop}
                                disabled={isExporting}
                                className="d-flex align-items-center gap-2 shadow-sm"
                                title="Xuất tổng hợp văn bằng + chứng chỉ đạt yêu cầu cho từng người"
                            >
                                {isExporting ? <Spinner animation="border" size="sm" /> : <i className="fas fa-file-excel" />}
                                {isExporting ? 'Đang xuất...' : 'Xuất Excel đạt yêu cầu'}
                            </Button>
                            <Button
                                variant="info"
                                size="sm"
                                onClick={handleExportTongHopDuyet}
                                disabled={isExportingDuyet}
                                className="d-flex align-items-center gap-2 shadow-sm text-white"
                                title="Xuất tổng hợp người đã được duyệt"
                            >
                                {isExportingDuyet ? <Spinner animation="border" size="sm" /> : <i className="fas fa-file-excel" />}
                                {isExportingDuyet ? 'Đang xuất...' : 'Xuất Excel đã duyệt'}
                            </Button>
                        </div>
                    </div>
                    {donViStats.length === 0 ? (
                        <div className="text-center text-muted py-4">Không có dữ liệu</div>
                    ) : <div className="table-responsive">
                        <Table bordered hover className="align-middle mb-0" style={{ fontSize: '0.9rem' }}>
                            <thead className="table-light">
                                <tr className="text-nowrap fw-bold">
                                    <th className="text-center" style={{ width: 50, fontWeight: 700 }}>STT</th>
                                    <th className="text-start" style={{ fontWeight: 700 }}>Đơn vị</th>
                                    <th className="text-center" style={{ minWidth: 120, fontWeight: 700 }}>Số người nộp</th>
                                    <th className="text-center" style={{ minWidth: 140, fontWeight: 700 }}>Số người phù hợp</th>
                                    <th className="text-center" style={{ minWidth: 160, fontWeight: 700 }}>Số người Không phù hợp</th>
                                    <th className="text-center" style={{ minWidth: 160, fontWeight: 700 }}>Số người chưa duyệt</th>
                                </tr>
                            </thead>
                            <tbody>
                                {donViStats.map((row, idx) => {
                                    const stt = (currentPage - 1) * itemsPerPage + idx + 1;
                                    return (
                                        <tr key={row.tenDonVi} style={{ color: '#000' }}>
                                            <td className="text-center" style={{ color: '#000' }}>{stt}</td>
                                            <td style={{ color: '#000' }}>
                                                <span className="fw-semibold">{row.tenDonVi}</span>
                                            </td>
                                            <td className="text-center" style={{ color: '#000' }}>
                                                <Badge style={{ fontSize: '0.85rem', padding: '5px 12px', color: '#fff', backgroundColor: '#667eea', borderRadius: 50 }}>
                                                    {row.soNguoi}
                                                </Badge>
                                            </td>
                                            <td className="text-center" style={{ color: '#000' }}>
                                                {row.soPhuHop > 0 ? (
                                                    <Badge style={{ fontSize: '0.85rem', padding: '5px 12px', color: '#fff', backgroundColor: '#11998e', borderRadius: 50 }}>
                                                        {row.soPhuHop}
                                                    </Badge>
                                                ) : (
                                                    <span style={{ color: '#000' }}>0</span>
                                                )}
                                            </td>
                                            <td className="text-center" style={{ color: '#000' }}>
                                                {row.soKhongPhuHop > 0 ? (
                                                    <Badge style={{ fontSize: '0.85rem', padding: '5px 12px', color: '#fff', backgroundColor: '#e53935', borderRadius: 50 }}>
                                                        {row.soKhongPhuHop}
                                                    </Badge>
                                                ) : (
                                                    <span style={{ color: '#000' }}>0</span>
                                                )}
                                            </td>
                                            <td className="text-center" style={{ color: '#000' }}>
                                                {row.soChuaDuyet > 0 ? (
                                                    <Badge style={{ fontSize: '0.85rem', padding: '5px 12px', color: '#fff', backgroundColor: '#ff9800', borderRadius: 50 }}>
                                                        {row.soChuaDuyet}
                                                    </Badge>
                                                ) : (
                                                    <span style={{ color: '#000' }}>0</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>

                        {/* Phân trang đã được cập nhật */}
                        {totalPages > 1 && (
                            <div className="d-flex justify-content-end mt-3">
                                <div
                                    className="dataTables_paginate paging_simple_numbers"
                                    id="example2_paginate"
                                >
                                    <Pagination
                                        itemClass="page-item"
                                        linkClass="page-link"
                                        innerClass="pagination pagination-sm no-margin pull-right"
                                        activePage={currentPage}
                                        itemsCountPerPage={Number(
                                            itemsPerPage
                                        )}
                                        totalItemsCount={totalDonVi}
                                        pageRangeDisplayed={5}
                                        onChange={(number) =>
                                            setCurrentPage(number)
                                        }
                                    />
                                </div>
                            </div>
                        )}
                    </div>}
                </Card.Body>
            </Card>

            <style>{`
                .thongke-section-title {
                    font-size: 0.8rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    color: #6c757d;
                    margin-bottom: 12px;
                }

                /* Pagination styling */
                .pagination .page-link {
                    color: #0d6efd;
                    border-radius: 6px;
                    margin: 0 2px;
                    border: 1px solid #dee2e6;
                    padding: 5px 11px;
                    font-weight: 500;
                    transition: all 0.18s ease;
                }
                .pagination .page-item.active .page-link {
                    background-color: #0d6efd;
                    border-color: #0d6efd;
                    color: #fff;
                }
                .pagination .page-item.disabled .page-link {
                    color: #6c757d;
                    background-color: #fff;
                    border-color: #dee2e6;
                }
                .pagination .page-link:hover:not(.disabled) {
                    background-color: #e9ecef;
                    border-color: #dee2e6;
                }
            `}</style>
        </div>
    );
};

export default ThongKe;